import React, { type FC, type JSX } from 'react';

import { formatTimeTwoDigit } from '@/app/components/search/utils/dates/utilDates';
import { Text } from '@lmn/design-system';
import { FullTransportLogos } from '@lmn/lmn-components';
import { isUsLocale } from '@/app/common/measures/locale.ts';
import {
  FlightContainer,
  TransportDuration,
  TransportInfo,
  TransportInfoAirports,
  TransportInfoTime,
  TransportPlusDays,
  TransportStops,
  TransportTime,
} from './styled';
import { calcDeltaDays, decorateAge, WayStops } from './utils';
import Baggage from '../Baggage';
import type { TransportProps } from '..';

const Transport: FC<TransportProps> = (props): JSX.Element => {
  const {
    isOutBound,
    departureDate,
    arrivalDate,
    departureAirportCode,
    departureAirportName,
    differentDepartureAirportCodes,
    differentArrivalAirportCodes,
    arrivalAirportCode,
    arrivalAirportName,
    numberOfStops,
    durationInMinutes,
    locale,
    dictionary,
    baggage,
    companies,
    isMixedWay,
    numberOfBaggagePiecesIncluded,
  } = props;
  const age = decorateAge(durationInMinutes, dictionary);
  const days = departureDate && arrivalDate ? calcDeltaDays(departureDate, arrivalDate) : null;
  const stops = WayStops({ numberOfStops, dictionary });
  const flightInfoTimeText = `${formatTimeTwoDigit(departureDate, locale)} – ${formatTimeTwoDigit(
    arrivalDate,
    locale
  )}`;
  // const flightInfoAirportsText = `(${departureAirportCode}) ${departureAirportName} – (${arrivalAirportCode}) ${arrivalAirportName}`;
  const flightInfoAirportsText = getTransportHubName({
    isOutBound,
    differentDepartureAirportCodes,
    differentArrivalAirportCodes,
    departureAirportCode,
    departureAirportName,
    arrivalAirportCode,
    arrivalAirportName,
  });

  // @ts-ignore
  return (
    <FlightContainer>
      <FullTransportLogos companies={companies} isMixedWay={isMixedWay} />
      <TransportInfo>
        <TransportInfoTime>
          <TransportTime>
            {flightInfoTimeText}
            <TransportPlusDays isUS={isUsLocale(locale)}>
              <Text variant="Title5">{days ? `+${days}` : null}</Text>
            </TransportPlusDays>
          </TransportTime>
          <TransportStops>
            <Text variant="BodySmall" weight="bold">
              {stops}
            </Text>
          </TransportStops>
          <TransportDuration>
            <Text variant="BodySmall" weight="bold">
              {age}
            </Text>
          </TransportDuration>
        </TransportInfoTime>
        <TransportInfoAirports>
          <Text variant="BodySmall" color="textSecondary">
            {flightInfoAirportsText}
          </Text>
        </TransportInfoAirports>
      </TransportInfo>
      <Baggage baggage={baggage} desktop={true} numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded} />
    </FlightContainer>
  );
};

function getTransportHubName({
  isOutBound,
  differentDepartureAirportCodes,
  differentArrivalAirportCodes,
  departureAirportCode,
  departureAirportName,
  arrivalAirportCode,
  arrivalAirportName,
}: {
  isOutBound: boolean | undefined;
  differentDepartureAirportCodes: boolean;
  differentArrivalAirportCodes: boolean;
  departureAirportCode: string;
  departureAirportName: string;
  arrivalAirportCode: string;
  arrivalAirportName: string;
}) {
  return (
    <>
      <span
        className={
          (isOutBound && differentDepartureAirportCodes) || (!isOutBound && differentArrivalAirportCodes)
            ? 'warning'
            : ''
        }
      >
        {`(${departureAirportCode})`}
      </span>
      {` ${departureAirportName} - `}
      <span
        className={
          (!isOutBound && differentDepartureAirportCodes) || (isOutBound && differentArrivalAirportCodes)
            ? 'warning'
            : ''
        }
      >
        {`(${arrivalAirportCode})`}
      </span>
      {` ${arrivalAirportName}`}
    </>
  );
}

export default Transport;
